import { useQuery } from '@tanstack/react-query';
import { Loader2, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { settingsApi } from '@/api/settings';
import { MaintenanceAlert } from '../../../components/admin/settings/MaintenanceAlert';
import { SettingsTabs } from '../../../components/admin/settings/SettingsTabs';
import { Button } from '../../../components/ui/button';

interface SettingsPageProps {
  canManage: boolean;
  isSuperAdmin: boolean;
  selectedEventId?: string;
}

export function SettingsPage({ canManage, isSuperAdmin, selectedEventId }: SettingsPageProps) {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['settings', 'global', selectedEventId],
    queryFn: () => settingsApi.getSettings({ scope: 'GLOBAL', eventId: selectedEventId }),
  });

  const maintenanceEnabled = Boolean(
    data?.find((setting) => setting.key === 'system.maintenance.mode')?.value
  );

  const handleRefresh = async () => {
    const result = await refetch();
    if (result.error) {
      toast.error('No fue posible recargar configuraciones.');
      return;
    }
    toast.success('Configuraciones recargadas.');
  };

  if (isLoading) {
    return (
      <div className="flex h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6 animate-in fade-in-50 duration-300">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Configuración Global</h1>
          <p className="text-muted-foreground">
            Gestiona mantenimiento, seguridad y parametros visuales del sistema.
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={isFetching}>
          <RefreshCcw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Recargar
        </Button>
      </div>

      <MaintenanceAlert isEnabled={maintenanceEnabled} />

      <SettingsTabs settings={data ?? []} canManage={canManage} isSuperAdmin={isSuperAdmin} />
    </div>
  );
}
